import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../../../elements/ui/card';
import EmptyState from './EmptyState';
import { useTranslation } from 'react-i18next';
import { dailyRows, dayAxisProps, hasValues, type TimeRange } from './timeSeries';
import type { LineSeries } from '../../../models/time-series';

interface Co2DailySpreadChartProps {
  title: string;
  series: LineSeries[];
  range: TimeRange;
  unit?: string;
}

function SpreadTooltip({ active, payload, label, unit, seriesName }: any) {
  if (!active || !payload?.length) {
    return null;
  }

  const row = payload[0]?.payload;
  const spread = row?.[`${seriesName}Spread`];
  const min = row?.[`${seriesName}Min`];
  const max = row?.[`${seriesName}Max`];

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-md text-xs">
      <p className="font-medium text-gray-700 mb-1">{label}</p>
      <div className="flex items-center gap-1.5">
        <div className="w-2 h-2 rounded-full bg-red-900" />
        <span className="text-gray-600">Spread:</span>
        <span className="font-medium text-gray-900">{spread != null ? `${spread} ${unit}` : '—'}</span>
      </div>
      {min != null && (
        <div className="text-gray-400 ml-3.5">
          Min: {min} {unit} / Max: {max} {unit}
        </div>
      )}
    </div>
  );
}

const Co2DailySpreadChart: React.FC<Co2DailySpreadChartProps> = ({ title, series, range, unit = 'ppm' }) => {
  const { t } = useTranslation();

  // CO2 is hundreds to thousands of ppm; a decimal on the spread is noise on the bar label.
  const dailyData = React.useMemo(() => dailyRows(series, range, 0), [series, range]);
  const hasData = hasValues(series);
  const seriesName = series[0]?.name;

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <EmptyState
            title={`${t('f-no-data-placeholder', { value: title.toUpperCase() })}`}
            description={`${t('f-sensor-daily-averages-unavailable')}`}
          />
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis {...dayAxisProps} />
              <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={40} />
              <Tooltip content={<SpreadTooltip unit={unit} seriesName={seriesName} />} />
              <Bar
                dataKey={`${seriesName}Spread`}
                barSize={20}
                radius={[4, 4, 0, 0]}
                label={{ position: 'top', fontSize: 10, fill: '#6b7280' }}
              >
                {dailyData.map((_entry, index) => (
                  <Cell key={index} fill="#7f1d1d" />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default Co2DailySpreadChart;
